import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { AuthenticationResult } from '@azure/msal-browser';
import { catchError, from, switchMap, throwError } from 'rxjs';
import { AuthSession } from '../auth/auth-session';
import { AuthRequiredError, acquireApiToken, getAuthRedirectError, signInForToken } from '../auth/ensure-signed-in';
import { environment } from '../../environments/environment';

export const apiInterceptor: HttpInterceptorFn = (request, next) => {
  if (!request.url.startsWith(environment.observabilityService.url)) {
    return next(request);
  }

  let headers = request.headers.set('Api-Version', environment.observabilityService.version);
  if (environment.apim.subscriptionKey) {
    headers = headers.set('Ocp-Apim-Subscription-Key', environment.apim.subscriptionKey);
  }

  if (!environment.auth.applicationId) {
    return next(request.clone({ headers }));
  }

  const msal = inject(MsalService, { optional: true });
  if (!msal) {
    return next(request.clone({ headers }));
  }

  const session = inject(AuthSession);

  const send = (result: AuthenticationResult) =>
    next(
      request.clone({
        headers: headers.set('Authorization', `Bearer ${result.accessToken}`)
      })
    );

  return from(acquireApiToken(msal.instance)).pipe(
    switchMap((result) =>
      send(result).pipe(
        catchError((error: unknown) => {
          if (!isUnauthorized(error)) {
            return throwError(() => error);
          }

          return from(acquireApiToken(msal.instance, true)).pipe(
            switchMap((refreshed) => send(refreshed)),
            catchError((refreshError: unknown) => {
              if (refreshError instanceof AuthRequiredError) {
                return throwError(() => refreshError);
              }

              if (isUnauthorized(refreshError)) {
                const account = msal.instance.getActiveAccount() ?? msal.instance.getAllAccounts()[0];
                if (account) {
                  signInForToken(msal.instance, account);
                }

                return throwError(() => new AuthRequiredError());
              }

              return throwError(() => refreshError);
            })
          );
        })
      )
    ),
    catchError((error: unknown) => {
      if (error instanceof AuthRequiredError) {
        session.markSignedOut(getAuthRedirectError() ?? error.message);
      }

      return throwError(() => error);
    })
  );
};

function isUnauthorized(error: unknown): boolean {
  return error instanceof HttpErrorResponse && error.status === 401;
}
